import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { api } from '@/runtime'
import type { SecurityClient, SecuritySummary } from '@/db/types'
import type { SettingsSpec } from '@/kinds/settings'
import { useMeta } from '@/db/hooks'
import { META } from '@/db/schema'
import { healthDot } from '@/lib/colour'
import { Button, cx } from '@/components/ui'
import { Phrases } from '@/components/Phrases'

/** How a hook source is spelt, not how its key is. */
const HOOK_NAMES: Record<string, string> = { github: 'GitHub', forgejo: 'Forgejo', factory: 'Factory' }

/** The security headers on the app shell, read off a HEAD of it. */
export interface SecurityHeaders {
  csp: boolean
  hsts: boolean
  frame: boolean
}

/**
 * The server's security summary (`/api/config` `security`) and the headers on
 * the app shell: undefined while asking, null when the server did not answer.
 * Settings → Security reads it.
 */
export const useSecurity = (reload = 0) => {
  const [sec, setSec] = useState<SecuritySummary | null | undefined>(undefined)
  const [headers, setHeaders] = useState<SecurityHeaders | undefined>()
  useEffect(() => {
    api
      .config()
      .then((c) => setSec(c.security ?? null))
      .catch(() => setSec(null))
    // the headers are on every response; read them off the app shell itself
    fetch('/', { method: 'HEAD', cache: 'no-store' })
      .then((r) =>
        setHeaders({
          csp: !!r.headers.get('content-security-policy'),
          hsts: !!r.headers.get('strict-transport-security'),
          frame: r.headers.get('x-frame-options') === 'DENY',
        }),
      )
      .catch(() => setHeaders(undefined))
  }, [reload])
  return { sec, headers }
}

/** One protection fact: what the server can see about itself. */
export interface SecurityFact {
  id: string
  ok: boolean
  /** a "no" that is fine in some setups: shown grey, not amber */
  soft?: boolean
  label: string
  detail: string
}

/** The protection facts, in the order Settings → Security lists them. Facts only. */
export const protectionFacts = (
  sec: SecuritySummary,
  headers: SecurityHeaders | undefined,
): SecurityFact[] => [
  {
    id: 'cloudflare',
    ok: sec.via_cloudflare,
    soft: !sec.via_cloudflare,
    label: sec.via_cloudflare ? 'Reached through Cloudflare' : 'Not reached through Cloudflare',
    detail: sec.via_cloudflare
      ? 'This request came through the edge; the origin refuses anything else.'
      : 'This request did not come through Cloudflare. Over Tailscale or on a laptop that is expected.',
  },
  {
    id: 'db-tls',
    ok: sec.db_tls,
    label: sec.db_tls ? 'Database connection encrypted' : 'Database traffic in the clear',
    detail: sec.db_tls
      ? 'Postgres traffic is on TLS.'
      : 'Postgres traffic is in the clear (sslmode=disable).',
  },
  {
    id: 'headers',
    ok: !!headers?.csp && !!headers.hsts && !!headers.frame,
    label: !headers
      ? 'Security headers'
      : headers.csp && headers.hsts && headers.frame
        ? 'Security headers set'
        : 'Security headers missing',
    detail: headers
      ? `${headers.csp ? 'CSP' : 'no CSP'}, ${headers.hsts ? 'HSTS' : 'no HSTS'}, ${headers.frame ? 'no framing' : 'framing allowed'}`
      : 'Checking…',
  },
  {
    id: 'token',
    ok: sec.token_length >= 32,
    label: 'Master token',
    detail: `${sec.token_length} characters${sec.token_length >= 32 ? '' : ', under 32'}. Only ever sent as a bearer header.`,
  },
  ...Object.entries(sec.hooks_signed).map(([name, on]) => ({
    id: `hook-${name}`,
    ok: on,
    soft: !on,
    label: `${HOOK_NAMES[name] ?? name.charAt(0).toUpperCase() + name.slice(1)} webhooks ${on ? 'signed' : 'not set up'}`,
    detail: on ? 'HMAC checked on every delivery.' : 'No hook secret set, so every delivery is refused.',
  })),
]

/**
 * Settings → Security: what the server can see about its own protection,
 * plus two things it cannot check for you but can show (who holds the token,
 * how many share links are open). Facts only; the reasoning is in docs/security.md.
 */
export const SecurityPanel = ({
  settings,
  save,
}: {
  settings: SettingsSpec
  save: (p: Partial<SettingsSpec>) => Promise<void>
}) => {
  const [draft, setDraft] = useState('')
  const [reload, setReload] = useState(0)
  const { sec, headers } = useSecurity(reload)
  const [blockErr, setBlockErr] = useState<string | null>(null)
  const blocked = settings.blocked_ips ?? []
  const block = async (entry: string) => {
    const v = entry.trim()
    setBlockErr(null)
    if (!v) return
    if (!/^[0-9a-fA-F.:]+(\/\d{1,3})?$/.test(v)) {
      setBlockErr('An address like 203.0.113.9, or a range like 203.0.113.0/24.')
      return
    }
    if (blocked.includes(v)) return
    await save({ blocked_ips: [...blocked, v] })
    setDraft('')
  }
  const unblock = (entry: string) => save({ blocked_ips: blocked.filter((b) => b !== entry) })
  const deviceId = useMeta<string>(META.deviceId)
  if (sec === undefined) return null
  if (sec === null)
    return (
      <section className="mb-6" data-testid="security-section">
        <h2 className="text-sm font-medium mb-2">Security</h2>
        <p className="text-xs text-faint">Could not reach the server.</p>
      </section>
    )
  const when = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '')
  return (
    <div data-testid="security-section">
      <section className="mb-6">
        <h2 className="text-sm font-medium mb-1">Protection</h2>
        <p className="text-xs text-faint mb-3">
          What the server can see about itself right now. The reasoning behind each is in the Security notes
          page under the docs.
        </p>
        <ul className="group-list">
          {protectionFacts(sec, headers).map((f) => (
            <Check key={f.id} {...f} />
          ))}
        </ul>
      </section>

      <section className="mb-6" data-testid="security-brake">
        <h2 className="text-sm font-medium mb-1">Wrong tokens</h2>
        <p className="text-xs text-muted">
          {sec.brake.failures === 0 ? (
            'None since the server started.'
          ) : (
            <>
              <span className="text-fg tabular-nums">{sec.brake.failures}</span> since the server started,
              last at {when(sec.brake.last_failed_at)}.
            </>
          )}{' '}
          A client gets {sec.brake.per_minute} a minute, then a minute of refusals
          {sec.brake.braked_clients > 0 ? (
            <>
              ; <span className="text-fg tabular-nums">{sec.brake.braked_clients}</span> braked right now
            </>
          ) : (
            ''
          )}
          . Your own token always passes.
        </p>
      </section>

      <section className="mb-6" data-testid="security-devices">
        <h2 className="text-sm font-medium mb-1">Devices holding the token</h2>
        <p className="text-xs text-faint mb-2">
          Forget clears a row left behind by a reinstall. A device still in use puts itself back, with its
          notifications, the next time the app opens there. To actually cut a device off, change the token in
          the server’s secrets and sign every device in again.
        </p>
        <ul className="group-list">
          {sec.devices.map((d) => (
            <li
              key={d.id}
              className="px-3.5 py-2 min-h-12 flex items-center justify-between gap-3 text-sm"
              data-device-id={d.id}
              data-current={d.id === deviceId ? 'yes' : 'no'}
            >
              <span className="min-w-0 truncate">
                {d.name || d.id}
                {d.app === 'prime' && <span className="ml-2 text-[11px] text-muted">Prime app</span>}
                {d.id === deviceId && <span className="ml-2 text-[11px] text-accent-text">this device</span>}
              </span>
              <span className="shrink-0 flex items-center gap-2 text-xs text-faint">
                <Phrases parts={[d.last_seen ? when(d.last_seen) : 'Not synced yet', d.push && 'Push']} />
                {d.id !== deviceId && (
                  <button
                    type="button"
                    onClick={() =>
                      void api
                        .forgetDevice(d.id)
                        .then(() => setReload((n) => n + 1))
                        .catch(() => {})
                    }
                    className="text-muted hover:text-fg min-h-8 px-1"
                    data-testid={`security-forget-${d.id}`}
                  >
                    Forget
                  </button>
                )}
              </span>
            </li>
          ))}
          {sec.devices.length === 0 && (
            <li className="px-3.5 py-2 text-xs text-faint">No devices registered.</li>
          )}
        </ul>
      </section>

      <section className="mb-6" data-testid="security-offenders">
        <h2 className="text-sm font-medium mb-1">Addresses that presented a wrong token</h2>
        <p className="text-xs text-faint mb-2">
          Since the server started, newest first. Country is what Cloudflare says. Blocking refuses the
          address on every path; your own token still passes, so you cannot lock yourself out.
        </p>
        <ClientList
          clients={sec.offenders}
          empty="None."
          action={(c) =>
            blocked.includes(c.ip) || c.blocked ? (
              <span className="text-[11px] text-faint">Blocked</span>
            ) : (
              <Button variant="chip" onClick={() => void block(c.ip)} data-testid={`security-block-${c.ip}`}>
                Block
              </Button>
            )
          }
        />
      </section>

      <section className="mb-6" data-testid="security-blocklist">
        <h2 className="text-sm font-medium mb-1">Blocked</h2>
        <p className="text-xs text-faint mb-2">
          Takes effect on the server within a few seconds and syncs like any setting. You are on{' '}
          <span className="font-mono text-fg">{sec.your_ip}</span> right now.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void block(draft)
          }}
          className="flex gap-2 mb-2"
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="203.0.113.9 or 203.0.113.0/24"
            className="flex-1 min-w-0 font-mono text-sm"
            autoCapitalize="off"
            spellCheck={false}
            data-testid="security-block-input"
          />
          <Button type="submit" disabled={!draft.trim()} data-testid="security-block-add">
            Block
          </Button>
        </form>
        {blockErr && <p className="text-xs text-rose-300 mb-2">{blockErr}</p>}
        <ul className={cx(blocked.length > 0 && 'group-list')}>
          {blocked.map((b) => (
            <li
              key={b}
              className="px-3.5 py-2 min-h-12 flex items-center justify-between gap-3 text-sm"
              data-blocked-ip={b}
            >
              <span className="font-mono">{b}</span>
              <button
                type="button"
                onClick={() => void unblock(b)}
                className="text-xs text-muted hover:text-fg min-h-8 px-2"
                data-testid={`security-unblock-${b}`}
              >
                Unblock
              </button>
            </li>
          ))}
          {blocked.length === 0 && <li className="text-xs text-faint">Nothing blocked.</li>}
        </ul>
      </section>

      <section className="mb-6" data-testid="security-trusted">
        <h2 className="text-sm font-medium mb-1">Where your token has come from</h2>
        <p className="text-xs text-faint mb-2">
          Your devices, by address. One you do not expect is worth a look.
        </p>
        <ClientList clients={sec.trusted} empty="Nothing yet." />
      </section>

      <section className="mb-6" data-testid="security-grants">
        <h2 className="text-sm font-medium mb-1">Open doors</h2>
        <p className="text-xs text-muted">
          <span className="text-fg tabular-nums">{sec.grants_live}</span> live share{' '}
          {sec.grants_live === 1 ? 'link' : 'links'}
          {sec.grants_total > sec.grants_live
            ? ` (${sec.grants_total - sec.grants_live} revoked, expired or used up)`
            : ''}
          . Each reaches one thing and can be revoked in{' '}
          <Link to="/settings/$section" params={{ section: 'sharing' }} className="underline hover:text-fg">
            Sharing
          </Link>
          .
        </p>
      </section>
    </div>
  )
}

const Check = ({
  ok,
  soft,
  label,
  detail,
  id,
}: {
  ok: boolean
  /** a "no" that is fine in some setups: shown grey, not amber */
  soft?: boolean
  label: string
  detail: string
  id: string
}) => (
  <li
    className="px-3.5 py-2.5 flex items-start gap-2.5"
    data-testid={`security-${id}`}
    data-ok={ok ? 'yes' : 'no'}
  >
    <span
      className={cx('mt-1.5 h-2 w-2 shrink-0 rounded-full', healthDot[ok ? 'ok' : soft ? 'off' : 'warn'])}
      aria-hidden
    />
    <span className="min-w-0">
      <span className="block text-sm">{label}</span>
      <span className="block text-xs text-muted">{detail}</span>
    </span>
  </li>
)

const ClientList = ({
  clients,
  empty,
  action,
}: {
  clients: SecurityClient[]
  empty: string
  action?: (c: SecurityClient) => React.ReactNode
}) => {
  const when = (iso: string) => new Date(iso).toLocaleString()
  if (clients.length === 0) return <p className="text-xs text-faint">{empty}</p>
  return (
    <ul className="group-list">
      {clients.map((c) => (
        <li
          key={c.ip}
          className="px-3.5 py-2 min-h-12 flex items-center justify-between gap-3"
          data-client-ip={c.ip}
        >
          <span className="min-w-0">
            <span className="block text-sm font-mono truncate">
              {c.ip}
              {c.country && <span className="ml-2 font-sans text-[11px] text-muted">{c.country}</span>}
            </span>
            <Phrases
              className="text-xs text-faint"
              parts={[
                `${c.count === 1 ? 'Once' : `${c.count} times`}, last ${when(c.last_at)}`,
                c.last_path,
                c.agent && <span className="block max-w-[16rem] truncate">{c.agent}</span>,
              ]}
            />
          </span>
          {action && <span className="shrink-0">{action(c)}</span>}
        </li>
      ))}
    </ul>
  )
}
